import { useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";

/**
 * Reusable searchable dropdown (Popover + Command).
 * Supports options as {id,name} or {value,label}. Preserves value/onChange contract.
 *
 * Props:
 *  - value, onChange(value)
 *  - options: array of {id|value, name|label}
 *  - placeholder, searchPlaceholder, emptyText
 *  - disabled, loading
 *  - emptyOption: optional {value,label} rendered as a selectable "clear/all" item
 *  - testid, className, triggerClassName
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Pilih...",
  searchPlaceholder = "Cari...",
  emptyText = "Tidak ditemukan.",
  disabled = false,
  loading = false,
  emptyOption = null,
  testid,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false);

  const norm = (o) => ({ value: String(o.value ?? o.id), label: o.label ?? o.name });
  const items = options.map(norm);
  const all = emptyOption ? [{ value: String(emptyOption.value ?? ""), label: emptyOption.label }, ...items] : items;
  const selected = all.find((o) => o.value === String(value ?? ""));
  const isDisabled = disabled || loading;

  const select = (v) => { onChange && onChange(v); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(o) => !isDisabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={isDisabled}
          aria-expanded={open}
          className={cn(
            "w-full flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-left outline-none transition-colors focus:ring-2 focus:ring-primary focus:bg-background disabled:opacity-50 disabled:cursor-not-allowed",
            triggerClassName
          )}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          {loading
            ? <Loader2 size={16} className="animate-spin text-muted-foreground shrink-0" />
            : <ChevronDown size={16} className="text-muted-foreground shrink-0" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[12rem]"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {all.map((o) => (
                <CommandItem
                  key={o.value || "__empty__"}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => select(o.value)}
                  data-testid={testid ? `${testid}-option-${o.value || "empty"}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4", selected?.value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
